import React, { useState, useEffect, useContext } from "react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import { makeStyles, Paper, Tabs, Tab, Typography, Box } from "@material-ui/core";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import BusinessIcon from "@material-ui/icons/Business";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PaletteIcon from "@material-ui/icons/Palette";
import ExtensionIcon from "@material-ui/icons/Extension";
import ScheduleIcon from "@material-ui/icons/Schedule";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";
import FinalizacaoAtendimento from "../../components/Settings/FinalizacaoAtendimento";
import Integrations from "../../components/Settings/Integrations";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: "#f5f7fb",
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    flex: 1,
    padding: theme.spacing(0, 4, 4),
    backgroundColor: "#f5f7fb",
    boxShadow: "none",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  tab: {
    display: "inline-flex",
    width: "auto",
    maxWidth: "100%",
    backgroundColor: "#f1f3f7",
    borderRadius: 14,
    padding: theme.spacing(0.5),
    minHeight: 46,
    marginBottom: theme.spacing(2.5),
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTabs-flexContainer": {
      gap: theme.spacing(0.5),
    },
    "& .MuiTab-root": {
      minHeight: 38,
      minWidth: "auto",
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 700,
      fontSize: 14,
      color: "#737985",
      padding: theme.spacing(0.75, 1.75),
      opacity: 1,
    },
    "& .MuiTab-wrapper": {
      flexDirection: "row",
      gap: theme.spacing(0.75),
      alignItems: "center",
      whiteSpace: "nowrap",
    },
    "& .MuiSvgIcon-root": {
      fontSize: 18,
      marginBottom: "0 !important",
    },
    "& .Mui-selected": {
      backgroundColor: "#fff",
      color: "#282a32",
      boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
    },
  },
  paper: {
    ...theme.scrollbarStyles,
    overflowY: "visible",
    padding: 0,
    display: "block",
    width: "100%",
    backgroundColor: "transparent",
  },
  container: {
    width: "100%",
    maxHeight: "100%",
  },
  control: {
    padding: theme.spacing(1),
  },
  textfield: {
    width: "100%",
  },
  header: {
    padding: theme.spacing(4, 4, 2.5),
    backgroundColor: "#f5f7fb",
    alignItems: "flex-start",
    display: "block",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(3, 2, 2),
    },
  },
  title: {
    fontWeight: 800,
    color: "#20222a",
    letterSpacing: 0,
    fontSize: 26,
    lineHeight: 1.2,
  },
  subtitle: {
    color: "#64748b",
    marginTop: 6,
    fontSize: 14,
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();

  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      if (!user || !user.companyId) {
        return;
      }

      setLoading(true);
      try {
        const companyId = user.companyId;

        const company = await find(companyId);
        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
  }, []);

  useEffect(() => {
    if (!socket || !user || !user.companyId) return;
    const onSettingsEvent = () => {
      getAllSettingsOld().then(setOldSettings);
    };
    socket.on(`company-${user.companyId}-settings`, onSettingsEvent);
    return () => {
      socket.off(`company-${user.companyId}-settings`, onSettingsEvent);
    };
  }, [socket, user]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success("Horários atualizados com sucesso.");
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = () => {
    return currentUser && currentUser.super;
  };

  return (
    <MainContainer className={classes.root}>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader className={classes.header}>
            <Box>
              <Typography variant="h5" className={classes.title}>
                Administração
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Gerencie empresas, planos, pagamentos, integrações e personalização.
              </Typography>
            </Box>
          </MainHeader>
          <Paper className={classes.mainPaper} elevation={1}>
            <Tabs
              value={tab}
              indicatorColor="primary"
              textColor="primary"
              scrollButtons="on"
              variant="scrollable"
              onChange={handleTabChange}
              className={classes.tab}
            >
              <Tab
                icon={<SettingsOutlinedIcon />}
                label={i18n.t("settings.tabs.options")}
                value={"options"}
              />
              {schedulesEnabled && (
                <Tab icon={<ScheduleIcon />} label="Horários" value={"schedules"} />
              )}
              {user.profile === "admin" &&
                user.finalizacaoComValorVendaAtiva && (
                  <Tab
                    icon={<AssignmentTurnedInIcon />}
                    label="Finalização do Atendimento"
                    value={"finalizacao"}
                  />
                )}
              {isSuper() ? (
                <Tab
                  icon={<BusinessIcon />}
                  label={i18n.t("settings.tabs.companies")}
                  value={"companies"}
                />
              ) : null}
              {isSuper() ? (
                <Tab
                  icon={<CreditCardIcon />}
                  label={i18n.t("settings.tabs.plans")}
                  value={"plans"}
                />
              ) : null}
              {isSuper() ? (
                <Tab
                  icon={<ExtensionIcon />}
                  label="Integrações"
                  value={"integrations"}
                />
              ) : null}
              {isSuper() ? (
                <Tab
                  icon={<HelpOutlineIcon />}
                  label={i18n.t("settings.tabs.helps")}
                  value={"helps"}
                />
              ) : null}
              {isSuper() ? (
                <Tab icon={<PaletteIcon />} label="Whitelabel" value={"whitelabel"} />
              ) : null}
            </Tabs>
            <Paper className={classes.paper} elevation={0}>
              {/* Renderização condicional simples - apenas a aba ativa renderiza */}
              {tab === "schedules" && (
                <div className={classes.container}>
                  <SchedulesForm
                    loading={loading}
                    onSubmit={handleSubmitSchedules}
                    initialValues={schedules}
                  />
                </div>
              )}

              {tab === "companies" && isSuper() && (
                <div className={classes.container}>
                  <CompaniesManager />
                </div>
              )}

              {tab === "plans" && isSuper() && (
                <div className={classes.container}>
                  <PlansManager />
                </div>
              )}

              {tab === "integrations" && isSuper() && (
                <div className={classes.container}>
                  <Integrations oldSettings={oldSettings} />
                </div>
              )}

              {tab === "helps" && isSuper() && (
                <div className={classes.container}>
                  <HelpsManager />
                </div>
              )}

              {tab === "whitelabel" && isSuper() && (
                <div className={classes.container}>
                  <Whitelabel settings={oldSettings} />
                </div>
              )}

              {tab === "finalizacao" && (
                <div className={classes.container}>
                  <FinalizacaoAtendimento
                    settings={settings}
                    onSettingsChange={(newSettings) => setSettings(newSettings)}
                  />
                </div>
              )}

              {tab === "options" && (
                <div className={classes.container}>
                  <Options
                    settings={settings}
                    oldSettings={oldSettings}
                    user={currentUser}
                    scheduleTypeChanged={(value) =>
                      setSchedulesEnabled(value === "company")
                    }
                  />
                </div>
              )}
            </Paper>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default SettingsCustom;
